import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Wallet, Briefcase, Plane, Quote, ShieldCheck, Smile } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { compact, label, metaFor, slugTestId } from "./cityMeta";

const tile = "rounded-lg border border-stone-200 bg-white p-5 sm:p-6 min-w-0";

export function Source({ children, href }) {
  const body = <span className="font-data text-[11px] text-stone-400 italic">Source: {children}</span>;
  return href ? <a href={href} target="_blank" rel="noreferrer" className="hover:underline">{body}</a> : body;
}

export function Bar({ value, max = 10, color, testid }) {
  return (
    <div className="h-2 w-full rounded-full bg-stone-100 overflow-hidden" data-testid={testid}>
      <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
    </div>
  );
}

function TileHead({ icon: Icon, children, color }) {
  return (
    <p className="font-data text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4" style={{ color }} /> {children}
    </p>
  );
}

export function HeroTile({ c, badge }) {
  const m = metaFor(c);
  return (
    <div className="relative overflow-hidden rounded-lg min-h-[320px] lg:min-h-[380px] bg-stone-900 text-white lg:col-span-8 min-w-0" data-testid={`featured-cities-city-header-${c.slug}`}>
      {m.photo && <img src={m.photo} alt={m.alt} className="absolute inset-0 h-full w-full object-cover" data-testid="featured-cities-skyline-image" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
      {badge && <div className="absolute top-4 right-4 z-10">{badge}</div>}
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 gap-4">
        <span className="font-data text-xs uppercase tracking-widest px-2 py-1 rounded self-start" style={{ background: m.accent }}>{c.country}</span>
        <h3 className="font-editorial text-4xl sm:text-5xl lg:text-6xl leading-none text-white" data-testid="featured-cities-city-name">{c.name}</h3>
        {c.vibe && <p className="text-sm sm:text-base text-stone-200 max-w-2xl" data-testid="featured-cities-tagline">{c.vibe}</p>}
        <div className="flex flex-wrap gap-6 font-data" data-testid="featured-cities-population-stat">
          <span><strong className="text-2xl sm:text-3xl">{compact(c.population)}</strong> <span className="text-xs text-stone-300">residents</span></span>
          <span><strong className="text-2xl sm:text-3xl">{compact(c.student_population)}</strong> <span className="text-xs text-stone-300">students</span></span>
        </div>
      </div>
    </div>
  );
}

export function KpiTile({ c }) {
  const { formatFrom } = useCurrency();
  const s = c.study;
  const kpis = [
    ["Universities", s.universities, "kpi-universities"],
    ["Courses", s.courses.toLocaleString(), "kpi-courses"],
    ["Avg grad salary", s.avg_starting_salary_usd ? formatFrom(s.avg_starting_salary_usd, "USD") : "—", "salary-stat"],
    ["Employed after grad", s.employment_rate ? `${Math.round(s.employment_rate * 100)}%` : "—", "kpi-employment"],
  ];
  return (
    <div className="rounded-lg bg-stone-900 text-stone-100 p-6 lg:col-span-4 grid grid-cols-2 gap-5 content-between min-w-0" data-testid="featured-cities-kpi-tile">
      {kpis.map(([k, v, id]) => (
        <div key={k} className="min-w-0">
          <p className="font-data text-3xl sm:text-4xl font-semibold tracking-tight break-words" data-testid={`featured-cities-${id}`}>{v}</p>
          <p className="font-data text-[11px] uppercase tracking-widest text-stone-400 mt-1">{k}</p>
        </div>
      ))}
      <p className="col-span-2 font-data text-[11px] text-stone-500 italic">
        Source: WUA university records{s.salary_sample ? ` · salary from ${s.salary_sample} universities with data` : ""}
      </p>
    </div>
  );
}

export function StudyTile({ c, accent }) {
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-cities-study-block">
      <TileHead icon={GraduationCap} color={accent}>Study</TileHead>
      <p className="text-sm text-stone-600 mb-3">Most-offered subjects</p>
      <div className="space-y-3 mb-6">
        {c.study.top_subjects.map((s) => (
          <div key={s.name} data-testid={`featured-cities-subject-${slugTestId(s.name)}`}>
            <p className="flex items-baseline justify-between text-sm mb-1"><span className="text-stone-800">{s.name}</span><span className="font-data text-xs text-stone-500">{s.courses.toLocaleString()} courses</span></p>
            <Bar value={s.courses} max={c.study.top_subjects[0]?.courses || 1} color={accent} />
          </div>
        ))}
      </div>
      <p className="text-sm text-stone-600 mb-2">Featured universities</p>
      <ul className="space-y-2">
        {c.study.featured.map((u) => (
          <li key={u.id}>
            <Link to={`/uni/${u.id}`} className="group flex items-center justify-between gap-2 text-stone-900 font-medium hover:underline" data-testid={`featured-cities-uni-link-${u.id}`}>
              <span className="truncate">{u.name}</span>
              <ArrowUpRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: accent }} />
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-6"><Source>WUA course database · {c.study.courses.toLocaleString()} courses at {c.study.universities} universities</Source></div>
    </div>
  );
}

function TempChart({ temps, accent }) {
  if (!temps?.length) return null;
  const max = Math.max(...temps, 1);
  return (
    <div data-testid="featured-cities-temp-chart">
      <div className="flex items-end gap-1 h-14">
        {temps.map((t, i) => (
          <div key={i} className="flex-1 rounded-t" title={`${"JFMAMJJASOND"[i]}: ${t}°C`} style={{ height: `${Math.max(8, (t / max) * 100)}%`, background: accent, opacity: 0.35 + 0.65 * (t / max) }} />
        ))}
      </div>
      <div className="flex gap-1 font-data text-[10px] text-stone-400 mt-1">{"JFMAMJJASOND".split("").map((m, i) => <span key={i} className="flex-1 text-center">{m}</span>)}</div>
      <p className="font-data text-[11px] text-stone-500 mt-1">Daily highs, °C · {Math.min(...temps)}° to {Math.max(...temps)}°</p>
    </div>
  );
}

function Score({ icon: Icon, name, value, accent, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <span className="text-sm text-stone-600">{name}</span>
      <span className="ml-auto flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <span key={i} className="h-2 w-4 rounded-sm" style={{ background: i <= (value || 0) ? accent : "#E7E5E4" }} />)}</span>
    </div>
  );
}

export function LivingTile({ c, accent }) {
  const { formatFrom } = useCurrency();
  const l = c.living;
  const f = (v) => (v == null ? "—" : formatFrom(v, l.currency));
  const mini = [["1-bed rent, centre", l.rent_1br_center], ["Student halls", l.student_accommodation], ["Coffee", l.coffee], ["Cheap meal", l.meal_cheap], ["Transport pass", l.monthly_transport]];
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-cities-living-block">
      <TileHead icon={Wallet} color={accent}>Living</TileHead>
      <p className="font-data text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900" data-testid="featured-cities-monthly-budget">{f(l.student_budget_mid)}</p>
      <p className="text-sm text-stone-600 mb-4">typical student budget / month</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {mini.filter(([, v]) => v != null).map(([k, v]) => (
          <div key={k} className="rounded px-2 py-1.5" style={{ background: metaFor(c).soft }}>
            <p className="font-data text-sm font-semibold text-stone-900">{f(v)}</p>
            <p className="text-[11px] text-stone-600">{k}</p>
          </div>
        ))}
        {l.hourly_wage != null && (
          <div className="rounded px-2 py-1.5 bg-stone-100" data-testid="featured-cities-hourly-wage">
            <p className="font-data text-sm font-semibold text-stone-900">{formatFrom(l.hourly_wage, l.wage_currency)}</p>
            <p className="text-[11px] text-stone-600">Part-time / hour</p>
          </div>
        )}
      </div>
      <div className="space-y-2 mb-4">
        <Score icon={ShieldCheck} name="Safety" value={l.safety} accent={accent} testid="featured-cities-safety-score" />
        <Score icon={Smile} name="Student-friendly" value={l.friendliness} accent={accent} testid="featured-cities-friendliness-score" />
      </div>
      <TempChart temps={l.temps_hi} accent={accent} />
      <div className="mt-3"><Source href={l.source_url}>Numbeo{l.as_of ? ` (${l.as_of})` : ""} · WUA city guide</Source></div>
    </div>
  );
}

export function IndustryTile({ c, accent }) {
  const { formatFrom } = useCurrency();
  const ind = c.industry;
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-cities-industry-block">
      <TileHead icon={Briefcase} color={accent}>Industry hub</TileHead>
      <div className="space-y-3 mb-5">
        {ind.sectors.map((s) => (
          <div key={s.tag}>
            <div className="flex items-baseline justify-between text-sm mb-1">
              <span className="text-stone-800">{label(s.tag)}</span>
              {s.rank && <span className="font-data text-xs" style={{ color: accent }} data-testid={`featured-cities-sector-rank-${s.tag}`}>#{s.rank} of {s.of} cities</span>}
            </div>
            <Bar value={s.score} color={accent} testid={`featured-cities-sector-bar-${s.tag}`} />
          </div>
        ))}
      </div>
      {ind.top_employers.length > 0 && (
        <>
          <p className="text-sm text-stone-600 mb-2">Where local graduates get hired</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {ind.top_employers.map((e) => (
              <span key={e} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: accent, color: accent }} data-testid={`featured-cities-employer-chip-${slugTestId(e)}`}>{e}</span>
            ))}
          </div>
        </>
      )}
      {ind.salary_facts.map((s) => (
        <Link key={s.firm_slug} to={`/employers/${s.firm_slug}`} className="block rounded border border-stone-200 px-3 py-2 mb-2 hover:bg-stone-50" data-testid={`featured-cities-salary-fact-${s.firm_slug}`}>
          <span className="font-data text-lg font-semibold text-stone-900">{formatFrom(s.base_annual, s.currency)}</span>
          <span className="text-xs text-stone-600"> base · {s.role} at {s.firm} ({s.location})</span>
        </Link>
      ))}
      <Source>WUA city career index · university recruiter lists · employer salary bands</Source>
    </div>
  );
}

function QuoteSource({ q }) {
  const text = `— ${q.source}${q.context ? ` · ${q.context}` : ""}`;
  return q.url
    ? <a href={q.url} target="_blank" rel="noreferrer" className="hover:underline">{text}</a>
    : <span>{text}</span>;
}

export function VisaQuoteTile({ c, accent }) {
  const [q, ...more] = c.quotes;
  return (
    <div className="rounded-lg border border-stone-200 bg-[#FBF8F3] p-5 sm:p-6 lg:col-span-8 grid gap-6 md:grid-cols-2 min-w-0" data-testid="featured-cities-lifestyle-block">
      <div className="min-w-0">
        <TileHead icon={Smile} color={accent}>Lifestyle scores</TileHead>
        <div className="grid grid-cols-2 gap-3">
          {c.lifestyle.map((s) => (
            <div key={s.tag} data-testid={`featured-cities-lifestyle-${s.tag}`}>
              <p className="flex items-baseline justify-between text-sm"><span className="text-stone-700">{label(s.tag)}</span><span className="font-data font-semibold">{s.score}/10</span></p>
              <Bar value={s.score} color={accent} />
            </div>
          ))}
        </div>
        {c.visa && (
          <div className="mt-5 rounded border-l-4 bg-white px-3 py-2" style={{ borderColor: accent }} data-testid="featured-cities-visa-badge">
            <p className="font-data text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> After you graduate</p>
            <p className="text-sm font-medium text-stone-900">{c.visa.route}</p>
            {c.visa.duration && <p className="text-xs text-stone-600">{c.visa.duration}</p>}
          </div>
        )}
      </div>
      {q && (
        <figure className="flex flex-col justify-center min-w-0" data-testid="featured-cities-quote">
          <Quote className="h-8 w-8 mb-2" style={{ color: accent }} />
          <blockquote className="font-editorial text-xl sm:text-2xl text-stone-900 leading-snug break-words" data-testid="featured-cities-quote-text">{q.quote}</blockquote>
          <figcaption className="font-data text-xs text-stone-500 mt-3"><QuoteSource q={q} /></figcaption>
          {more.length > 0 && (
            <ul className="mt-5 space-y-3 border-t border-stone-200 pt-4" data-testid="featured-cities-more-quotes">
              {more.slice(0, 2).map((m) => (
                <li key={m.quote} className="text-sm text-stone-700 break-words">
                  “{m.quote}” <span className="font-data text-[11px] text-stone-500"><QuoteSource q={m} /></span>
                </li>
              ))}
            </ul>
          )}
        </figure>
      )}
    </div>
  );
}

export function CtaTile({ c, slugs, share }) {
  return (
    <div className="rounded-lg bg-[#002147] text-white p-6 lg:col-span-4 flex flex-col justify-between gap-6 min-w-0" data-testid="featured-cities-cta-tile">
      <p className="font-editorial text-2xl sm:text-3xl leading-tight">Could {c.name} be your city?</p>
      <div className="flex flex-col gap-2">
        <Link to={`/cities/${c.slug}`} className="inline-flex items-center justify-between rounded-full bg-white text-[#002147] px-5 py-3 font-medium transition-transform hover:-translate-y-0.5" data-testid="featured-cities-explore-city-button">
          Explore {c.name} <ArrowUpRight className="h-4 w-4" />
        </Link>
        <Link to={`/cities/compare?slugs=${slugs.join(",")}`} className="inline-flex items-center justify-between rounded-full border border-white/40 px-5 py-3 text-sm transition-colors hover:bg-white/10" data-testid="featured-cities-compare-button">
          Compare all featured cities <ArrowUpRight className="h-4 w-4" />
        </Link>
        {share}
      </div>
    </div>
  );
}
